import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_SLIDES = ["/wayside/hero1.jpg", "/wayside/hero2.jpg"];
const STORY_IMGS = ["/wayside/beekeeping.jpg", "/wayside/syrup-boiler.jpg", "/wayside/maple-syrup.jpg"];

const CATEGORIES = [
  { slug: "honey", name: "Honey", img: "/wayside/beekeeping.jpg" },
  { slug: "cider", name: "Cider", img: "https://images.pexels.com/photos/34425143/pexels-photo-34425143.jpeg" },
  { slug: "maple-syrup", name: "Maple Syrup", img: "/wayside/maple-syrup.jpg" },
  { slug: "wine", name: "Wine", img: "https://images.pexels.com/photos/13622704/pexels-photo-13622704.jpeg?auto=compress&cs=tinysrgb&w=1080" },
  { slug: "apparel", name: "Apparel", img: "https://images.pexels.com/photos/18739741/pexels-photo-18739741.jpeg" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4)));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      {/* Hero carousel */}
      <section className="relative h-[88vh] min-h-[560px] flex items-center overflow-hidden" data-testid="hero-carousel">
        {HERO_SLIDES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt="Wayside Orchard"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms]"
            style={{ opacity: i === slide ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="relative px-6 md:px-12 lg:px-20 max-w-7xl mx-auto w-full">
          <div className="max-w-xl animate-fade-up">
            <p className="kicker text-[#F0D9BF] [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">Welcome to Wayside Orchard</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] mt-4 tracking-tight [text-shadow:0_2px_12px_rgba(0,0,0,0.4)]">
              Small Batch,<br />Hand Made.
            </h1>
            <p className="text-white/90 text-lg mt-6 leading-relaxed max-w-md [text-shadow:0_1px_6px_rgba(0,0,0,0.5)]">
              Honey, cider, maple syrup, wine and more — grown and made by hand in our little orchard.
            </p>
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="inline-flex items-center gap-2 mt-8 bg-[#B85C38] hover:bg-[#9A4A2B] text-white uppercase tracking-widest text-sm px-8 py-4 transition-colors rounded-sm"
            >
              Visit the Shop <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              data-testid={`hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? "w-8 bg-white" : "w-4 bg-white/50"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Story intro */}
      <section className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto py-20 md:py-32 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="grid grid-cols-2 gap-4">
          <img src={STORY_IMGS[0]} alt="Tending the bees" className="rounded-sm w-full h-72 object-cover col-span-2" />
          <img src={STORY_IMGS[1]} alt="Boiling maple syrup" className="rounded-sm w-full h-52 object-cover" />
          <img src={STORY_IMGS[2]} alt="Bottled maple syrup" className="rounded-sm w-full h-52 object-cover" />
        </div>
        <div>
          <p className="kicker">Welcome to Wayside</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2D372B] mt-3 tracking-tight leading-tight">
            The way things were, and will be.
          </h2>
          <p className="text-[#4A5D4E] text-lg leading-relaxed mt-6">
            Relax &amp; recharge. Whether visiting the orchard or enjoying some of its bounty, we hope you take a
            minute to pull over, get off the highway of life, and refill your proverbial cup.
          </p>
          <p className="kicker mt-8 text-[#7C897B]">Est. 2019</p>
          <Link
            to="/our-story"
            data-testid="home-about-link"
            className="inline-flex items-center gap-2 mt-4 text-[#B85C38] uppercase tracking-widest text-sm border-b border-[#B85C38] pb-1 hover:gap-3 transition-all"
          >
            About Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-[#F0EBE1] py-20 md:py-28">
        <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="kicker">Shop</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2D372B] mt-2 tracking-tight">Our Goods</h2>
            </div>
            <p className="text-[#4A5D4E] max-w-sm leading-relaxed">
              Join our family of nature lovers and enjoy the gifts this little orchard has to offer.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/shop?category=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className="group relative aspect-[3/4] overflow-hidden rounded-sm"
              >
                <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <span className="absolute bottom-4 left-4 font-serif text-2xl text-white">{c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto py-20 md:py-28">
        <div className="text-center mb-12">
          <p className="kicker">From the Orchard</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2D372B] mt-2 tracking-tight">Featured Goods</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/shop"
            data-testid="view-all-button"
            className="inline-flex items-center gap-2 border border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38] hover:text-white uppercase tracking-widest text-sm px-8 py-3.5 transition-colors rounded-sm"
          >
            View All Goods <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
